import { equals, copy, IPromise } from "angular";
import { v4 as uuidv4 } from "uuid";

import { IGoal } from "@gtmhub/goals/models";
import { IMetric } from "@gtmhub/okrs/metrics";
import { GoalService } from "@gtmhub/goals/goalService";
import { IAssigneeIdMap, AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import {
  IDraftGoalMetricState,
  IGoalState,
  IAsyncState,
  IAlertState,
  IWhiteboardShapeState,
  AlertType,
  zeroPosition,
  IImportedGoalMetricState,
  IImportedGoalState,
  IDraftGoalState,
  INoteState,
  IHeadingState,
} from "../shapes";
import { ProgressColoring } from "@gtmhub/configuration";
import { getCurrentUserId, UserService } from "@gtmhub/users";
import { localize } from "@gtmhub/localization";
import { getClosestOpenActiveSession, IPlanningSession, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { UIErrorHandlingService } from "@gtmhub/error-handling";

export const createDraftMetric = (options: { name: string; ownerId?: string; ownerIds?: string[] }): IDraftGoalMetricState => ({
  id: uuidv4(),
  name: options.name,
  description: "",
  ...(options.ownerIds ? { ownerIds: options.ownerIds } : { ownerIds: [options.ownerId] }),
  manualType: "double",
  target: 1,
  targetOperator: "at_least",
  initialValue: 0,
  format: { prefix: "", suffix: "", fractionSize: 0 },
  dueDate: null,
  softDueDate: null,
  customFields: null,
  orderId: null,
});

export const toAlertState = (shape: IWhiteboardShapeState, alertType: AlertType, message: string): IAlertState => {
  return {
    type: "alert",
    id: uuidv4(),
    alertType,
    position: shape.position,
    message,
    original: shape,
    createdById: shape.createdById,
  };
};

const bindStateToCtrl = (state: IAssigneesStoreState & ISessionsStoreState) => {
  return {
    assignees: state.assignees.map,
    sessions: state.sessions.items,
  };
};
export class ShapeStateConverter {
  // redux
  assignees: IAssigneeIdMap;
  sessions?: IPlanningSession[];
  defaultSession?: IPlanningSession;

  static $inject = ["$ngRedux", "AssigneeActions", "PlanningSessionsActions", "GoalService", "ProgressColoring", "UserService", "UIErrorHandlingService"];

  constructor(
    $ngRedux: INgRedux,
    assigneeActions: AssigneeActions,
    planningSessionsActions: PlanningSessionsActions,
    private goalService: GoalService,
    private progressColoring: ProgressColoring,
    private userService: UserService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    $ngRedux.connect(bindStateToCtrl)(this);
    $ngRedux.dispatch(assigneeActions.getAssignees());
    $ngRedux.dispatch(planningSessionsActions.getSessionsIfMissing());

    this.setDefaultSession();
  }

  private setDefaultSession(): void {
    this.userService.getUser(getCurrentUserId()).then(
      (user) => {
        this.defaultSession = getClosestOpenActiveSession(this.sessions, user);
      },
      (err) => this.uiErrorHandlingService.handleModal(err, { backdrop: false })
    );
  }

  toAsyncOrAlertState(shape: IWhiteboardShapeState): IAsyncState | IAlertState {
    if (shape.type !== "goal") {
      throw new Error(`Could not create an async shape for shape of type ${shape.type} with id ${shape.id}`);
    }

    const promise = this.goalService.getGoal(shape.id).then(
      (goal) => Object.assign(this.goalToState(goal), { position: shape.position, zIndex: shape.zIndex }),
      () => console.error(`Could not load Objective with id ${shape.id}`)
    );

    return {
      type: "async",
      id: uuidv4(),
      position: shape.position,
      message: `${localize("loading_objective")}...`,
      promise: promise as IPromise<IWhiteboardShapeState>,
      original: shape,
      createdById: shape.createdById,
      zIndex: shape.zIndex,
    };
  }

  metricToState(metric: IMetric): IImportedGoalMetricState {
    return {
      id: metric.id,
      name: metric.name,
      attainment: this.progressColoring.wrapProgress(metric.attainment),
      ownerIds: metric.ownerIds,
      manualType: metric.manualType,
      target: metric.target,
      targetOperator: metric.targetOperator,
      initialValue: metric.initialValue,
      description: metric.description,
      format: metric.format,
      dueDate: metric.dueDate,
      softDueDate: metric.softDueDate,
      customFields: metric.customFields,
      orderId: metric.orderId,
    };
  }

  goalToState(goal: IGoal): IImportedGoalState {
    const shape: IImportedGoalState = {
      type: "goal",
      id: goal.id,
      name: goal.name || "",
      ownerIds: goal.ownerIds,
      position: zeroPosition,
      metrics: (goal.metrics || []).map((metric) => this.metricToState(metric)),
      createdById: getCurrentUserId(),
      sessionId: goal.sessionId,
      attainmentTypeString: goal.attainmentTypeString,
      tags: copy(goal.tags) || [],
      description: goal.description,
      attainment: this.progressColoring.wrapProgress(goal.attainment),
      private: goal.private,
      customFields: goal.customFields,
    };
    if (goal.parentId) {
      shape.parent = { id: goal.parentId, type: goal.parentType };
    }
    return shape;
  }

  draftToGoal(goalState: IDraftGoalState): IGoal {
    return {
      draft: true,
      id: goalState.id,
      name: goalState.name,
      ownerIds: goalState.ownerIds,
      sessionId: goalState.sessionId,
      parentId: goalState.parent ? goalState.parent.id : "",
      parentType: goalState.parent ? goalState.parent.type : null,
      tags: copy(goalState.tags),
      private: goalState.private,
      customFields: goalState.customFields,
      attainmentTypeString: goalState.attainmentTypeString,
      description: goalState.description,
      metrics: goalState.metrics.map((metric) => this.draftToMetric(goalState, metric)),
    };
  }

  draftToMetric(goalState: IGoalState, metricState: IDraftGoalMetricState): IMetric {
    return {
      draft: true,
      id: metricState.id,
      name: metricState.name,
      ownerIds: metricState.ownerIds,
      target: metricState.target,
      targetOperator: metricState.targetOperator,
      manualType: metricState.manualType,
      sessionId: goalState.sessionId,
      goalId: goalState.id,
      goalName: goalState.name,
      initialValue: metricState.initialValue,
      description: metricState.description,
      format: metricState.format,
      dueDate: metricState.dueDate,
      softDueDate: metricState.softDueDate,
      customFields: metricState.customFields,
    };
  }

  createDraftGoal(name: string): IDraftGoalState {
    const currentUserId = getCurrentUserId();
    return {
      type: "draftGoal",
      id: uuidv4(),
      name,
      description: "",
      ownerIds: [currentUserId],
      position: zeroPosition,
      metrics: [],
      createdById: currentUserId,
      sessionId: this.defaultSession && this.defaultSession.id,
      attainmentTypeString: "average_kr",
      tags: [],
      private: false,
      customFields: null,
    };
  }

  createDraftGoalFromNoteOrHeading(shape: INoteState | IHeadingState): IDraftGoalState {
    const lines = (shape.content || "").trim().split(/(?:\r\n|\r|\n)/g);

    const goalName = lines.length ? lines[0] : "";
    const goal = Object.assign(this.createDraftGoal(goalName), { position: shape.position, zIndex: shape.zIndex });
    const owner = { ownerIds: goal.ownerIds };
    for (let i = 1; i < lines.length; i++) {
      goal.metrics.push(createDraftMetric({ name: lines[i].trim(), ...owner }));
    }

    return goal;
  }
}

export const getShapeChanges = (source: IWhiteboardShapeState, target: IWhiteboardShapeState): Record<string, unknown> => {
  const changes: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) {
    if (!equals(value, target[key])) {
      changes[key] = target[key];
    }
  }

  return changes;
};
